/**
 * Bundle kit builder (sections/bundle-kit-builder.liquid): the product being viewed plus 3 picks the
 * shopper can keep, swap one at a time, or replace all at once, sold together at the kit discount.
 *
 * Data: the section embeds the anchor product, the default picks and the metafield products as JSON.
 * Shopify recommendations are fetched from the section's /recommendations/products render and added
 * to the swap pool; they also fill any default pick the metafield didn't provide.
 *
 * Checkout (same rules as pdp-bundle.js): retail customers with an endpoint set go to the cambie-kits
 * draft-order API, which applies the kit discount per line server-side. Wholesale customers, or no
 * endpoint, use the regular cart through the items[] form of /cart/add (the Rapid Bundle app rewrites
 * a plain `quantity` add but leaves items[] alone).
 */
if (!customElements.get('bundle-kit-builder')) {
  const PAGE = 8;
  const DEBOUNCE = 150;
  const TOAST_MS = 7000;
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const mqMobile = matchMedia('(max-width: 767px)');
  const mqReduce = matchMedia('(prefers-reduced-motion: reduce)');
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const formatMoney = (cents, format) => {
    const fmt = (n, prec, th = ',', dec = '.') => {
      const [i, d] = (n / 100).toFixed(prec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (d ? dec + d : '');
    };
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: fmt(cents, 2),
      amount_no_decimals: fmt(cents, 0),
      amount_with_comma_separator: fmt(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: fmt(cents, 0, '.', ','),
      amount_with_apostrophe_separator: fmt(cents, 2, "'", '.'),
      amount_with_space_separator: fmt(cents, 2, ' ', ','),
    }[k] ?? fmt(cents, 2)));
  };

  const track = (name, detail = {}) => {
    window.dispatchEvent(new CustomEvent(name, { detail }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...detail });
  };

  const routes = () => (window.theme && window.theme.routes) || {};

  class BundleKitBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this._abort = new AbortController();

      const cfg = (this.cfg = JSON.parse(this.querySelector('[data-bkb-config]').textContent));
      this.keep = 100 - Number(cfg.discount || 0);
      this.products = new Map();
      this.poolIds = [];
      cfg.products.forEach((p) => this.addProduct(p));
      this.anchor = { pid: cfg.anchor.productId, vid: cfg.anchor.variantId };
      this.defaults = cfg.defaults.map((d) => ({ pid: d.productId, vid: d.variantId }));
      this.slots = this.defaults.map((d) => ({ ...d }));
      this.kitReady = this.defaults.length === 3;

      this.expanded = 0; // 0 = closed, 2..4 = open swap panel for that slot
      this.pickMode = false;
      this.picks = []; // [{pid, vid}], max 3
      this.q = { term: '', query: '', showAll: false };
      this.toast = null;
      this.searchCache = new Map(); // lower-cased term -> eligible products, or null while loading

      const $ = (s) => this.querySelector(s);
      this.el = {
        list: $('[data-list]'), pick: $('[data-pick]'), hint: $('[data-hint]'), multiBtn: $('[data-action="multi-toggle"]'),
        save: $('[data-save]'), total: $('[data-total]'), compare: $('[data-compare]'), discount: $('[data-discount]'), atcLabel: $('[data-atc-label]'),
        atcMain: $('[data-atc-main]'), msg: $('[data-msg]'), reset: $('[data-action="reset"]'), live: $('[data-live]'),
        stripSave: $('[data-strip-save]'), stripTotal: $('[data-strip-total]'), stripCompare: $('[data-strip-compare]'),
        sticky: $('[data-sticky]'), stickySave: $('[data-sticky-save]'), stickyTotal: $('[data-sticky-total]'),
        toast: $('[data-toast]'), toastText: $('[data-toast-text]'), pickTitle: $('.bkb-pick__title'), stack: $('[data-stack]'),
        headerSave: $('[data-header-save]'),
      };

      const { signal } = this._abort;
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('input', (e) => { if (e.target.matches('[data-search]')) this.onSearch(e.target); });
      this.addEventListener('keydown', (e) => this.onKeydown(e));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.onEscape(e); }, { signal });

      this.buildPool();
      if (this.kitReady) {
        this.renderRows();
        this.updateTotals();
      }
      this.syncHint();
      this.watchAnchorVariant();
      this.setupSticky();
      this.loadRecommendations();
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      if (this._observers) this._observers.forEach((o) => o.disconnect());
      clearTimeout(this._toastT);
      clearTimeout(this._searchT);
      if (this.pickMode) document.documentElement.style.overflow = this._overflow || '';
      document.body.classList.remove('bkb-in-view');
      this._ready = false;
    }

    /* ---------- data ---------- */
    addProduct(p) {
      if (this.products.has(p.id)) return;
      this.products.set(p.id, p);
      if (this.cfg.anchor.productId !== p.id) this.poolIds.push(p.id);
    }

    // Swap pool: every known product except the anchor, with only its available variants.
    buildPool() {
      this.eligible = this.poolIds
        .map((id) => this.products.get(id))
        .filter((p) => p.variants.some((v) => v.available))
        .map((p) => this.eligibleOf(p));
      const pop = this.eligible.filter((p) => p.popular);
      this.popular = pop.length ? pop : this.eligible.slice(0, 8);
      this.el.multiBtn.hidden = this.eligible.length < 3;
    }

    /*
     * Recommendations render this section as a bare JSON list. Each intent is fetched in turn and
     * merged into the pool. If the metafield gave fewer than 3 default picks, the first available
     * recommendations fill them; with fewer than 3 in total the kit can't be built and the section hides.
     */
    async loadRecommendations() {
      for (const intent of this.cfg.recIntents || []) {
        try {
          const res = await fetch(`${this.cfg.recsUrl}&intent=${encodeURIComponent(intent)}`);
          if (!res.ok) continue;
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await res.text();
          const json = tmpl.content.querySelector('[data-bkb-recs]');
          if (json) JSON.parse(json.textContent).forEach((p) => this.addProduct(p));
        } catch (e) { /* try the next intent */ }
      }
      if (!this.isConnected) return;
      this.buildPool();

      if (!this.kitReady) {
        const taken = new Set(this.defaults.map((d) => d.pid));
        for (const p of this.eligible) {
          if (this.defaults.length >= 3) break;
          if (taken.has(p.id)) continue;
          taken.add(p.id);
          this.defaults.push({ pid: p.id, vid: p.variants[0].id });
        }
        if (this.defaults.length < 3) {
          const section = this.closest('.shopify-section');
          (section || this).hidden = true;
          return;
        }
        this.slots = this.defaults.map((d) => ({ ...d }));
        this.kitReady = true;
        this.classList.remove('is-loading-kit');
        this.renderRows();
        this.updateTotals();
        this.syncSticky();
        return;
      }

      // Pool grew: refresh whatever picker is open.
      if (this.pickMode) this.renderPick();
      else if (this.expanded) this.renderResults();
    }

    eligibleOf(p) { return { ...p, variants: p.variants.filter((v) => v.available) }; }

    // Search results carry their collection handles, so the section's exclusions (bulk packs,
    // bundles, pallets) are applied here with the live settings.
    isExcluded(p) {
      const handles = this.cfg.excludeCollections || [];
      const words = (this.cfg.excludeKeywords || []).map((w) => w.trim()).filter(Boolean);
      const title = p.title.toLowerCase();
      return (p.collections || []).some((h) => handles.includes(h)) || words.some((w) => title.includes(w));
    }

    /*
     * Searches the whole store: this section, rendered on the search page by its file name, returns the
     * matching products as JSON. Results are cached per term and merged into the picker; the picker
     * re-renders when they arrive if the shopper is still on that term.
     */
    async searchStore(term) {
      const key = term.toLowerCase();
      if (this.searchCache.has(key)) return;
      this.searchCache.set(key, null);
      let found = [];
      try {
        const params = new URLSearchParams({ q: term, type: 'product', 'options[prefix]': 'last', section_id: 'bundle-kit-builder' });
        const res = await fetch(`${this.cfg.searchUrl}?${params}`);
        if (res.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await res.text();
          const json = tmpl.content.querySelector('[data-bkb-search]');
          if (json) {
            found = JSON.parse(json.textContent)
              .filter((p) => p.id !== this.anchor.pid && !this.isExcluded(p) && p.variants.some((v) => v.available));
          }
        }
      } catch (e) { /* fall back to the products already loaded */ }
      found.forEach((p) => { if (!this.products.has(p.id)) this.products.set(p.id, p); });
      this.searchCache.set(key, found.map((p) => this.eligibleOf(this.products.get(p.id))));
      if (!this.isConnected || this.q.query.toLowerCase() !== key || !this.ctx()) return;
      this.renderResults();
      if (this.ctx().querySelector('.bkb-empty')) track('kit_search_no_results', { term });
    }

    // Products matching the current search: what's already loaded first, then the store search results.
    searchMatches(t, excludeId) {
      const local = this.eligible.filter((p) => this.matches(p, t));
      const remote = this.searchCache.get(this.q.query.toLowerCase());
      const extra = (remote || []).filter((p) => !local.some((l) => l.id === p.id));
      return { list: [...local, ...extra].filter((p) => p.id !== excludeId), pending: !remote };
    }

    /* ---------- helpers ---------- */
    money(c) { return formatMoney(c, this.cfg.moneyFormat); }
    // Same as the draft-order API: each line's discount is rounded down to the cent.
    kit(c) { return c - Math.floor((c * (100 - this.keep)) / 100); }
    product(pid) { return this.products.get(pid); }
    variant(pid, vid) { const p = this.product(pid); return p.variants.find((v) => v.id === vid) || p.variants[0]; }
    totals(slots = this.slots) {
      const all = [this.anchor, ...slots].map((s) => this.variant(s.pid, s.vid).price);
      const compare = all.reduce((a, b) => a + b, 0);
      const total = all.reduce((a, b) => a + this.kit(b), 0);
      return { total, compare, save: compare - total };
    }
    isChanged() { return this.slots.some((s, i) => s.pid !== this.defaults[i].pid || s.vid !== this.defaults[i].vid); }
    imgHTML(p, v, wide) {
      const src = (v && v.image) || p.image;
      return `<span class="bkb-img${wide ? ' bkb-img--wide' : ''}">${src ? `<img src="${esc(src)}" alt="${esc(p.alt || p.title)}" width="480" height="${wide ? 360 : 480}" loading="lazy" decoding="async">` : ''}</span>`;
    }
    variantLabel(p, v) { return this.product(p.id).variants.length > 1 ? v.title : ''; }
    announce(msg) { this.el.live.textContent = ''; requestAnimationFrame(() => { this.el.live.textContent = msg; }); }
    saveText(t) { return this.cfg.retail ? `, you save ${this.money(t.save)}` : ''; }
    ctx() { return this.pickMode ? this.el.pick : this.querySelector('.bkb-swap'); }

    /* ---------- kit list ---------- */
    renderRows() {
      this.el.list.innerHTML = [1, 2, 3, 4].map((n) => this.rowHTML(n)).join('');
      this.syncExpanded();
      if (this.expanded) this.renderResults();
    }

    // Keep the markup in step with snippets/bundle-kit-row.liquid.
    rowHTML(n) {
      const isA = n === 1;
      const s = isA ? this.anchor : this.slots[n - 2];
      const p = this.product(s.pid);
      const v = this.variant(s.pid, s.vid);
      const k = this.kit(v.price);
      const isDefault = !isA && s.pid === this.defaults[n - 2].pid && s.vid === this.defaults[n - 2].vid;
      const tag = isA ? ['This item', 'is-anchor'] : isDefault ? ['Our pick', ''] : ['Your pick', 'is-mine'];
      const open = this.expanded === n;
      const meta = this.variantLabel(p, v);
      const saving = v.price - k;
      const price = `<span class="bkb-price"><span class="bkb-sr">Kit price </span>${esc(this.money(k))}</span><s class="bkb-compare"><span class="bkb-sr">Regular price </span>${esc(this.money(v.price))}</s>${saving > 0 ? `<span class="bkb-row__save">Save ${esc(this.money(saving))}</span>` : ''}`;
      const action = isA
        ? '<span class="bkb-row__lock">Size follows<br>your choice above</span>'
        : `<button type="button" class="bkb-btn bkb-btn--pill${open ? ' is-on' : ''}" data-action="swap-toggle" data-slot="${n}" aria-expanded="${open}" aria-controls="${this.id}-swap" aria-label="${open ? 'Close swap options for' : 'Swap'} ${esc(p.title)}">${open ? 'Close' : 'Swap'}</button>`;
      return `
        <li class="bkb-row" data-row="${n}">
          <div class="bkb-row__main">
            <span class="bkb-row__thumb">${this.imgHTML(p, v)}</span>
            <div class="bkb-row__info">
              <span class="bkb-row__tag ${tag[1]}">${tag[0]}</span>
              <h3 class="bkb-row__title">${esc(p.title)}</h3>
              ${meta ? `<span class="bkb-row__meta">${esc(meta)}</span>` : ''}
              <span class="bkb-row__price-inline">${price}</span>
            </div>
            <div class="bkb-row__price">${price}</div>
            <div class="bkb-row__action">${action}</div>
          </div>
          ${open ? this.swapPanelHTML(p) : ''}
        </li>`;
    }

    swapPanelHTML(p) {
      return `
        <div class="bkb-swap" id="${this.id}-swap" role="region" aria-label="Swap options for ${esc(p.title)}">
          <div class="bkb-swap__top">
            <div class="bkb-search">
              <label class="bkb-sr" for="${this.id}-q">Search products to swap in</label>
              <input type="search" id="${this.id}-q" class="bkb-search__input" placeholder="Search all products…" autocomplete="off" data-search>
              <button type="button" class="bkb-search__clear" data-action="clear" aria-label="Clear search" hidden>×</button>
            </div>
            <button type="button" class="bkb-link" data-action="keep">Keep current</button>
          </div>
          <p class="bkb-status" role="status" data-status></p>
          <div class="bkb-results" data-results></div>
        </div>`;
    }

    /* ---------- results (shared by the swap panel and pick mode) ---------- */
    tokens() { return this.q.query.toLowerCase().split(/\s+/).filter(Boolean); }
    matches(p, t) { const hay = `${p.title} ${p.type}`.toLowerCase(); return t.every((w) => hay.includes(w)); }

    renderResults() {
      const ctx = this.ctx();
      if (!ctx) return;
      const t = this.tokens();
      const inKit = new Set(this.slots.map((s) => s.pid));
      let list;
      let status = '';
      let empty = false;
      let pending = false;

      if (this.pickMode) {
        if (t.length) ({ list, pending } = this.searchMatches(t));
        else list = [...this.popular, ...this.eligible.filter((p) => !this.popular.includes(p))];
        if (t.length && !list.length && !pending) { empty = true; list = this.popular.slice(0, 4); }
        else if (t.length) status = `${list.length} result${list.length === 1 ? '' : 's'} for “${this.q.query}”`;
      } else {
        const cur = this.slots[this.expanded - 2];
        const curP = this.product(cur.pid);
        if (t.length) ({ list, pending } = this.searchMatches(t, cur.pid));
        else {
          // Suggested: same type as the item being replaced first, then popular picks.
          const sameType = this.eligible.filter((p) => p.type === curP.type && !inKit.has(p.id));
          const others = [...this.popular, ...this.eligible].filter((p, i, a) => a.indexOf(p) === i && p.type !== curP.type && !inKit.has(p.id));
          list = [...sameType, ...others].slice(0, 4);
        }
        if (t.length && !list.length && !pending) { empty = true; list = this.popular.filter((p) => !inKit.has(p.id)).slice(0, 4); }
        else if (t.length) status = `${list.length} result${list.length === 1 ? '' : 's'} for “${this.q.query}”`;
      }

      // While the store search runs, say so instead of announcing "0 results".
      if (t.length && pending) status = list.length ? `${status} · Searching all products…` : 'Searching all products…';
      ctx.querySelector('[data-status]').textContent = status;

      const shown = this.q.showAll ? list : list.slice(0, PAGE);
      const card = this.pickMode ? (p) => this.pickCardHTML(p) : (p) => this.swapCardHTML(p, inKit);
      ctx.querySelector('[data-results]').innerHTML = `
        ${empty ? `<div class="bkb-empty" role="status">
            <p class="bkb-empty__title">Nothing matches “${esc(this.q.query)}”</p>
            <p class="bkb-empty__text">Try “cream”, “nut” or “syrup”. Here's what bakers add most:</p>
            <button type="button" class="bkb-btn bkb-btn--outline" data-action="clear">Clear search</button>
          </div>` : ''}
        <div class="bkb-grid">${shown.map(card).join('')}</div>
        ${list.length > PAGE && !this.q.showAll ? `<button type="button" class="bkb-btn bkb-btn--outline bkb-more" data-action="more">Show all ${list.length}</button>` : ''}`;
    }

    deltaHTML(newKit, curKit) {
      const d = newKit - curKit;
      if (d === 0) return { html: '<span class="bkb-delta">Same</span>', sr: 'same price' };
      return d > 0
        ? { html: `<span class="bkb-delta">+${esc(this.money(d))}</span>`, sr: `${this.money(d)} more` }
        : { html: `<span class="bkb-delta is-down">−${esc(this.money(-d))}</span>`, sr: `${this.money(-d)} less` };
    }

    swapCardHTML(p, inKit) {
      if (inKit.has(p.id)) {
        return `<div class="bkb-card is-inkit" aria-disabled="true">${this.imgHTML(p, null, true)}<span class="bkb-card__title">${esc(p.title)}</span><span class="bkb-card__note">✓ Already in your kit</span></div>`;
      }
      const cur = this.slots[this.expanded - 2];
      const curKit = this.kit(this.variant(cur.pid, cur.vid).price);
      if (p.variants.length === 1) {
        const v = p.variants[0];
        const k = this.kit(v.price);
        const d = this.deltaHTML(k, curKit);
        return `<button type="button" class="bkb-card" data-action="choose" data-pid="${p.id}" data-vid="${v.id}" aria-label="Swap in ${esc(p.title)}, ${esc(this.money(k))}, ${esc(d.sr)}">
          ${this.imgHTML(p, v, true)}<span class="bkb-card__title">${esc(p.title)}</span>
          <span class="bkb-card__foot"><span class="bkb-card__price">${esc(this.money(k))}</span>${d.html}</span></button>`;
      }
      return `<div class="bkb-card">${this.imgHTML(p, null, true)}<span class="bkb-card__title">${esc(p.title)}</span>
        <div class="bkb-card__sizes">${p.variants.map((v) => {
          const k = this.kit(v.price);
          const d = this.deltaHTML(k, curKit);
          return `<button type="button" class="bkb-size" data-action="choose" data-pid="${p.id}" data-vid="${v.id}" aria-label="Swap in ${esc(p.title)}, ${esc(v.title)}, ${esc(this.money(k))}, ${esc(d.sr)}"><span>${esc(v.title)}</span><span>${esc(this.money(k))}</span></button>`;
        }).join('')}</div></div>`;
    }

    pickCardHTML(p) {
      const idx = this.picks.findIndex((x) => x.pid === p.id);
      const picked = idx >= 0;
      const locked = this.picks.length >= 3 && !picked;
      const badge = picked ? `<span class="bkb-badge">Item ${idx + 2}</span>` : '';
      const cls = `bkb-card is-pick${picked ? ' is-picked' : ''}${locked ? ' is-locked' : ''}`;
      if (p.variants.length === 1) {
        const v = p.variants[0];
        const k = this.money(this.kit(v.price));
        const label = picked ? `Remove ${p.title} from picks` : locked ? `${p.title}, 3 items already picked` : `Pick ${p.title}, ${k}`;
        return `<button type="button" class="${cls}" data-action="pick" data-pid="${p.id}" data-vid="${v.id}" aria-pressed="${picked}" aria-disabled="${locked}" aria-label="${esc(label)}">
          ${this.imgHTML(p, v, true)}${badge}<span class="bkb-card__title">${esc(p.title)}</span>
          <span class="bkb-card__foot"><span class="bkb-card__price">${esc(k)}</span><span class="bkb-ind" aria-hidden="true">${picked ? '✓' : '+'}</span></span></button>`;
      }
      return `<div class="${cls}">${this.imgHTML(p, null, true)}${badge}<span class="bkb-card__title">${esc(p.title)}</span>
        <div class="bkb-card__sizes">${p.variants.map((v) => {
          const on = picked && this.picks[idx].vid === v.id;
          const k = this.money(this.kit(v.price));
          return `<button type="button" class="bkb-size" data-action="pick" data-pid="${p.id}" data-vid="${v.id}" aria-pressed="${on}" aria-disabled="${locked}" aria-label="${on ? 'Remove' : 'Pick'} ${esc(p.title)}, ${esc(v.title)}, ${esc(k)}"><span>${esc(v.title)}</span><span>${esc(k)}</span></button>`;
        }).join('')}</div></div>`;
    }

    /* ---------- single-slot swap ---------- */
    openSwap(n) {
      if (this.pickMode) this.closePick(false);
      this.expanded = n;
      this.q = { term: '', query: '', showAll: false };
      this.renderRows();
      const input = this.querySelector('.bkb-swap [data-search]');
      if (input && !mqMobile.matches) input.focus({ preventScroll: true });
      track('kit_picker_open', { mode: 'single', slot: n, product_id: this.slots[n - 2].pid });
    }
    closeSwap(focus = true) {
      const n = this.expanded;
      this.expanded = 0;
      this.renderRows();
      if (focus && n) this.focusSwap(n);
    }
    focusSwap(n) {
      const b = this.querySelector(`[data-action="swap-toggle"][data-slot="${n}"]`);
      if (b) b.focus({ preventScroll: true });
    }

    swapTo(n, pid, vid) {
      const prev = { ...this.slots[n - 2] };
      const from = this.product(prev.pid);
      const to = this.product(pid);
      this.slots[n - 2] = { pid, vid };
      this.expanded = 0;
      this.renderRows();
      this.flashRows([n]);
      this.updateTotals();
      const t = this.totals();
      this.showToast({ type: 'single', slot: n, prev }, `Swapped in <strong>${esc(to.title)}</strong> · kit now ${esc(this.money(t.total))}`);
      this.announce(`${to.title} replaced ${from.title}. Kit total ${this.money(t.total)}${this.saveText(t)}. Undo available.`);
      this.focusSwap(n);
      track('kit_item_swap', { slot: n, from_product_id: from.id, to_product_id: pid, to_variant_id: vid });
    }

    flashRows(ns) {
      ns.forEach((n) => {
        const row = this.querySelector(`[data-row="${n}"]`);
        if (!row) return;
        row.classList.add('is-flash');
        setTimeout(() => row.classList.remove('is-flash'), 700);
        if (!mqReduce.matches) row.querySelector('.bkb-row__thumb').classList.add('is-swapping');
      });
    }

    /* ---------- pick all 3 ---------- */
    openPick() {
      if (this.expanded) { this.expanded = 0; this.renderRows(); }
      this.pickMode = true;
      this.picks = [];
      this.q = { term: '', query: '', showAll: false };
      const input = this.el.pick.querySelector('[data-search]');
      input.value = '';
      this.el.pick.querySelector('[data-action="clear"]').hidden = true;
      this.el.list.hidden = true;
      this.el.pick.hidden = false;
      this.el.multiBtn.textContent = 'Cancel';
      this.el.multiBtn.classList.add('is-on');
      this.el.multiBtn.setAttribute('aria-expanded', 'true');
      this.syncHint();
      if (mqMobile.matches) {
        this.el.pick.setAttribute('role', 'dialog');
        this.el.pick.setAttribute('aria-modal', 'true');
        this._overflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.el.pick.scrollTop = 0;
        this.el.pickTitle.focus();
      } else {
        this.el.pick.setAttribute('role', 'region');
        this.el.pick.removeAttribute('aria-modal');
        input.focus({ preventScroll: true });
      }
      this.renderPick();
      this.syncExpanded();
      this.syncSticky();
      track('kit_picker_open', { mode: 'all' });
    }
    closePick(focus = true) {
      this.pickMode = false;
      this.picks = [];
      this.el.pick.hidden = true;
      this.el.list.hidden = false;
      this.el.multiBtn.textContent = 'Pick all 3 yourself';
      this.el.multiBtn.classList.remove('is-on');
      this.el.multiBtn.setAttribute('aria-expanded', 'false');
      this.syncHint();
      document.documentElement.style.overflow = this._overflow || '';
      this.syncExpanded();
      this.syncSticky();
      if (focus) this.el.multiBtn.focus({ preventScroll: true });
    }
    // While a picker is open the left column grows tall, so the summary stops stretching to its
    // height and sticks instead, keeping the add-to-cart button in view.
    syncExpanded() { this.classList.toggle('is-expanded', !!this.expanded || this.pickMode); }

    // Mobile shows a plain "Your kit" label above the list; desktop explains the two ways to change it.
    syncHint() {
      if (mqMobile.matches) this.el.hint.textContent = 'Your kit';
      else this.el.hint.textContent = this.pickMode ? 'Your current kit stays until you confirm.' : 'Swap one item, or replace all three at once.';
    }
    togglePick(pid, vid) {
      const i = this.picks.findIndex((x) => x.pid === pid);
      if (i >= 0) {
        if (this.picks[i].vid === vid) this.picks.splice(i, 1);
        else this.picks[i] = { pid, vid };
      } else if (this.picks.length < 3) this.picks.push({ pid, vid });
      else return false;
      this.renderPick();
      return true;
    }
    renderPick() {
      const n = this.picks.length;
      const full = n === 3;
      const status = this.el.pick.querySelector('[data-pick-status]');
      status.textContent = full ? '3 of 3 picked ✓' : `${n} of 3 picked`;
      status.classList.toggle('is-done', full);
      this.el.pick.querySelectorAll('[data-pick-segs] span').forEach((s, i) => s.classList.toggle('is-on', i < n));

      const a = this.product(this.anchor.pid);
      const av = this.variant(this.anchor.pid, this.anchor.vid);
      const tiles = [`<div class="bkb-tile is-anchor"><span class="bkb-tile__thumb">${this.imgHTML(a, av)}</span><span class="bkb-tile__text"><span class="bkb-tile__label">Always included</span><span class="bkb-tile__title">${esc(a.title)}${a.variants.length > 1 ? ` - ${esc(av.title)}` : ''}</span></span></div>`];
      for (let k = 0; k < 3; k++) {
        const pk = this.picks[k];
        if (!pk) {
          tiles.push(`<div class="bkb-tile${k === n ? ' is-next' : ''}"><span class="bkb-tile__plus" aria-hidden="true">+</span><span class="bkb-tile__text"><span class="bkb-tile__label">Item ${k + 2}</span><span class="bkb-tile__title">${k === n ? (mqMobile.matches ? '+ Tap below' : 'Tap a product below') : 'Empty'}</span></span></div>`);
        } else {
          const p = this.product(pk.pid);
          const v = this.variant(pk.pid, pk.vid);
          const title = p.variants.length > 1 ? `${p.title} - ${v.title}` : p.title;
          tiles.push(`<div class="bkb-tile is-filled"><span class="bkb-tile__thumb">${this.imgHTML(p, v)}</span><span class="bkb-tile__text"><span class="bkb-tile__label">Item ${k + 2} · ${esc(this.money(this.kit(v.price)))}</span><span class="bkb-tile__title">${esc(title)}</span></span><button type="button" class="bkb-tile__remove" data-action="tray-remove" data-pid="${pk.pid}" aria-label="Remove ${esc(title)}">×</button></div>`);
        }
      }
      this.el.pick.querySelector('[data-tray]').innerHTML = tiles.join('');

      const foot = this.el.pick.querySelector('[data-pick-foot]');
      if (full) {
        const t = this.totals(this.picks);
        foot.textContent = `New kit total ${this.money(t.total)}${this.cfg.retail ? ` · you save ${this.money(t.save)}` : ''}`;
      } else foot.textContent = `Pick ${3 - n} more item${3 - n === 1 ? '' : 's'} to see your new total`;
      foot.classList.toggle('is-done', full);
      this.el.pick.querySelector('[data-action="pick-apply"]').setAttribute('aria-disabled', String(!full));
      this.renderResults();
    }
    applyPicks() {
      if (this.picks.length !== 3) return;
      const prev = this.slots.map((s) => ({ ...s }));
      this.slots = this.picks.map((p) => ({ ...p }));
      this.closePick(false);
      this.renderRows();
      this.flashRows([2, 3, 4]);
      this.updateTotals();
      const t = this.totals();
      this.showToast({ type: 'all', prev }, `Kit updated with <strong>3 new items</strong> · ${esc(this.money(t.total))}`);
      this.announce(`Kit updated with 3 new items. Kit total ${this.money(t.total)}${this.saveText(t)}. Undo available.`);
      this.el.multiBtn.focus({ preventScroll: true });
      track('kit_pick_all_apply', { from: prev.map((s) => s.pid), to: this.slots.map((s) => s.pid) });
    }

    /* ---------- undo / reset ---------- */
    showToast(data, html) {
      this.toast = data;
      this.el.toastText.innerHTML = html;
      this.el.toast.hidden = false;
      clearTimeout(this._toastT);
      this._toastT = setTimeout(() => this.hideToast(), TOAST_MS);
    }
    hideToast() { this.toast = null; this.el.toast.hidden = true; }
    undo() {
      const t = this.toast;
      if (!t) return;
      this.hideToast();
      if (t.type === 'all') {
        this.slots = t.prev;
        this.renderRows();
        this.flashRows([2, 3, 4]);
        this.el.multiBtn.focus({ preventScroll: true });
      } else {
        this.slots[t.slot - 2] = t.prev;
        this.renderRows();
        this.flashRows([t.slot]);
        this.focusSwap(t.slot);
      }
      this.updateTotals();
      this.announce(`Change undone. Kit total ${this.money(this.totals().total)}.`);
      track('kit_undo', { type: t.type });
    }
    reset() {
      const changed = [2, 3, 4].filter((n) => {
        const s = this.slots[n - 2];
        const d = this.defaults[n - 2];
        return s.pid !== d.pid || s.vid !== d.vid;
      });
      this.slots = this.defaults.map((d) => ({ ...d }));
      this.expanded = 0;
      this.hideToast();
      this.renderRows();
      this.flashRows(changed);
      this.updateTotals();
      this.announce(`Kit reset to our picks. Kit total ${this.money(this.totals().total)}.`);
      this.focusSwap(2);
      track('kit_reset');
    }

    /* ---------- totals ---------- */
    updateTotals() {
      if (!this.kitReady) return;
      const { total, compare, save } = this.totals();
      const T = this.money(total);
      const C = this.money(compare);
      const S = this.money(save);
      this.el.save.textContent = S;
      this.el.total.textContent = T;
      this.el.compare.textContent = C;
      this.el.discount.textContent = `−${S}`;
      this.el.stripSave.textContent = `You save ${S}`;
      if (this.el.headerSave) this.el.headerSave.textContent = S;
      this.el.stripTotal.textContent = T;
      this.el.stripCompare.textContent = C;
      this.el.stickySave.textContent = `You save ${S}`;
      this.el.stickyTotal.textContent = T;
      const soldOut = !this.variant(this.anchor.pid, this.anchor.vid).available;
      this.el.atcLabel.textContent = soldOut ? 'Selected size is sold out' : `Add kit to cart · ${T}`;
      this.querySelectorAll('[data-action="atc"]').forEach((b) => { b.disabled = soldOut || !!this._busy; });
      this.el.reset.hidden = !this.isChanged();
      this.el.stack.innerHTML = this.lines().map((s) => {
        const p = this.product(s.pid);
        const v = this.variant(s.pid, s.vid);
        const src = v.image || p.image;
        return `<span class="bkb__stack-thumb">${src ? `<img src="${esc(src)}" alt="" width="48" height="48" loading="lazy" decoding="async">` : ''}</span>`;
      }).join('');
    }

    /* ---------- events ---------- */
    onClick(e) {
      const btn = e.target.closest('[data-action]');
      if (!btn || !this.contains(btn) || !this.kitReady) return;
      const a = btn.dataset.action;
      const pid = Number(btn.dataset.pid);
      const vid = Number(btn.dataset.vid);
      switch (a) {
        case 'swap-toggle': {
          const n = Number(btn.dataset.slot);
          if (this.expanded === n) this.closeSwap();
          else this.openSwap(n);
          break;
        }
        case 'keep': this.closeSwap(); break;
        case 'choose': this.swapTo(this.expanded, pid, vid); break;
        case 'clear':
          this.q.term = this.q.query = '';
          this.clearInput();
          if (this.pickMode) this.renderPick();
          else this.renderResults();
          this.ctx().querySelector('[data-search]').focus();
          break;
        case 'more': this.q.showAll = true; this.renderResults(); break;
        case 'multi-toggle':
          if (this.pickMode) this.closePick();
          else this.openPick();
          break;
        case 'pick':
          if (btn.getAttribute('aria-disabled') === 'true') return;
          this.togglePick(pid, vid);
          this.refocus(`[data-action="pick"][data-pid="${pid}"][data-vid="${vid}"]`);
          break;
        case 'tray-remove': {
          const i = this.picks.findIndex((x) => x.pid === pid);
          if (i >= 0) this.picks.splice(i, 1);
          this.renderPick();
          this.el.pick.querySelector('[data-search]').focus({ preventScroll: true });
          break;
        }
        case 'pick-cancel': this.closePick(); break;
        case 'pick-apply': this.applyPicks(); break;
        case 'undo': this.undo(); break;
        case 'reset': this.reset(); break;
        case 'atc': this.addToCart(); break;
        default:
      }
    }
    refocus(sel) {
      const el = this.ctx() && this.ctx().querySelector(sel);
      if (el) el.focus({ preventScroll: true });
    }
    clearInput() {
      const ctx = this.ctx();
      if (!ctx) return;
      ctx.querySelector('[data-search]').value = '';
      ctx.querySelector('[data-action="clear"]').hidden = true;
    }
    onSearch(input) {
      this.q.term = input.value;
      this.ctx().querySelector('[data-action="clear"]').hidden = !input.value;
      clearTimeout(this._searchT);
      this._searchT = setTimeout(() => {
        this.q.query = input.value.trim();
        this.q.showAll = false;
        this.renderResults();
        if (this.q.query) {
          track('kit_search', { term: this.q.query, mode: this.pickMode ? 'all' : 'single' });
          this.searchStore(this.q.query);
        }
      }, DEBOUNCE);
    }
    onEscape(e) {
      if (this.pickMode) { e.preventDefault(); this.closePick(); }
      else if (this.expanded) { e.preventDefault(); this.closeSwap(); }
    }
    // Focus trap for the mobile pick-all sheet.
    onKeydown(e) {
      if (e.key !== 'Tab' || !this.pickMode || !mqMobile.matches) return;
      const items = [...this.el.pick.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null || n === document.activeElement);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !this.el.pick.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* ---------- the anchor follows the PDP variant ---------- */
    watchAnchorVariant() {
      const { signal } = this._abort;
      const update = (raw) => {
        const id = Number(raw);
        if (!id || id === this.anchor.vid) return;
        const p = this.product(this.anchor.pid);
        const v = p.variants.find((x) => x.id === id);
        if (!v) return;
        this.anchor = { pid: p.id, vid: v.id };
        if (!this.kitReady) return;
        const row = this.querySelector('[data-row="1"]');
        if (row) row.outerHTML = this.rowHTML(1);
        if (this.pickMode) this.renderPick();
        if (this.expanded) this.renderResults();
        this.updateTotals();
        this.announce(`${p.title} updated to ${v.title}. Kit total ${this.money(this.totals().total)}.`);
      };
      const isMainId = (n) => n && n.name === 'id' && n.closest('form[action*="/cart/add"]') && !this.contains(n);
      const fromEvent = (e) => update(e.detail && e.detail.variant && e.detail.variant.id);
      document.addEventListener('change', (e) => { if (isMainId(e.target)) update(e.target.value); }, { signal });
      // The theme's variant picker fires on:variant:change; variant:change covers other picker scripts.
      document.addEventListener('on:variant:change', fromEvent, { signal });
      document.addEventListener('variant:change', fromEvent, { signal });
    }

    /* ---------- add to cart ---------- */
    lines() { return [this.anchor, ...this.slots]; }

    async addToCart() {
      if (this._busy) return;
      this._busy = true;
      const btns = [...this.querySelectorAll('[data-action="atc"]')];
      btns.forEach((b) => { b.classList.add('is-loading'); b.setAttribute('aria-busy', 'true'); b.disabled = true; });
      this.el.msg.hidden = true;

      const lines = this.lines();
      const { total } = this.totals();
      const bundleId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const useDraft = this.cfg.retail && !!this.cfg.endpoint;
      let leaving = false;

      try {
        track('kit_add_to_cart', {
          bundle_id: bundleId,
          total: total / 100,
          currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || undefined,
          checkout: useDraft ? 'draft_order' : 'cart',
          items: lines.map((s, i) => ({ slot: i + 1, product_id: s.pid, variant_id: s.vid, title: this.product(s.pid).title, price: this.kit(this.variant(s.pid, s.vid).price) / 100 })),
        });
        leaving = useDraft ? await this.checkoutDraft(lines) : await this.addToThemeCart(lines);
      } catch (err) {
        this.showMsg(err.message || 'We couldn’t add the kit. Please try again.');
      } finally {
        // On a redirect the page is navigating away, so the button keeps its loading state.
        if (!leaving) {
          this._busy = false;
          btns.forEach((b) => { b.classList.remove('is-loading'); b.removeAttribute('aria-busy'); });
          this.updateTotals();
        }
      }
    }

    /*
     * Full kit for retail customers: the kit plus anything already in the cart goes to the draft-order
     * API, which discounts each kit line and returns a checkout URL. The cart is cleared afterwards,
     * because its items are now in that order. Resolves true when the page is redirecting.
     */
    async checkoutDraft(lines) {
      const cartUrl = routes().cart || '/cart';
      // Existing cart items go into the same order at full price. If the cart can't be read, stop
      // rather than check out without them.
      const cartRes = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
      if (!cartRes.ok) throw new Error('Could not read your cart. Please try again.');
      const cart = await cartRes.json();
      const cartItems = (cart.items || []).map((i) => {
        const line = { variant_id: i.variant_id, quantity: i.quantity };
        if (i.properties && Object.keys(i.properties).length) line.properties = i.properties;
        return line;
      });

      const payload = { shop: this.cfg.shop, kitItems: lines.map((s) => ({ variant_id: s.vid, quantity: 1 })) };
      if (cartItems.length) payload.cartItems = cartItems;

      const res = await fetch(this.cfg.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) throw new Error(data.error || 'Checkout failed. Please try again.');

      // A failed clear still redirects: the order matters more than the leftover cart.
      if (cartItems.length) {
        const clear = routes().cartClear;
        await fetch(clear ? `${clear}.js` : '/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } }).catch(() => {});
      }
      window.location.href = data.url;
      return true;
    }

    // Cart fallback (wholesale customers, or no endpoint). Resolves true when the page is redirecting.
    async addToThemeCart(lines) {
      const body = new FormData();
      lines.forEach((s, i) => {
        body.append(`items[${i}][id]`, s.vid);
        body.append(`items[${i}][quantity]`, 1);
      });
      // Ask for the sections the drawer needs back in the same request, as product-form.js does.
      const drawer = document.querySelector('cart-drawer');
      const drawerSection = drawer && drawer.closest('.shopify-section');
      let sections = 'cart-icon-bubble';
      if (drawerSection) sections += `,${drawerSection.id.replace('shopify-section-', '')}`;
      body.append('sections', sections);
      body.append('sections_url', window.location.pathname);

      const res = await fetch(routes().cartAdd || '/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'We couldn’t add the kit. Please try again.');

      const cartUrl = routes().cart || '/cart';
      // A discount code can only be applied by visiting /discount/CODE, so that route wins over the
      // drawer: charging the advertised price matters more than staying on the page.
      const code = this.cfg.retail ? this.cfg.discountCode : '';
      if (code || this.cfg.afterAdd === 'cart') {
        window.location.href = code
          ? `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(cartUrl)}`
          : cartUrl;
        return true;
      }
      if (!this.openDrawer(data)) {
        window.location.href = cartUrl;
        return true;
      }
      return false;
    }

    /*
     * Opens whichever cart drawer this store actually uses and reports whether one opened (same order
     * as pdp-bundle.js): Rebuy's smart cart through the cart icon, then the theme <cart-drawer>, then
     * the cart icon on its own. Rebuy's SmartCart.show() is deliberately not used: here it navigates to /cart.
     */
    openDrawer(data) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && data.sections && data.sections['cart-icon-bubble']) bubble.innerHTML = data.sections['cart-icon-bubble'];

      document.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { cart: data, variantId: data.items && data.items[0] && data.items[0].variant_id },
      }));

      const rebuy = window.Rebuy;
      const icon = document.getElementById('cart-icon');
      if (rebuy && icon) {
        try {
          if (rebuy.Cart && typeof rebuy.Cart.fetchCart === 'function') rebuy.Cart.fetchCart();
          icon.click();
          return true;
        } catch (e) { /* fall through to the theme drawer */ }
      }
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function') {
        try {
          drawer.renderContents(data, true);
          return true;
        } catch (e) { /* fall through to the cart icon */ }
      }
      if (icon) {
        icon.click();
        return true;
      }
      return false;
    }

    showMsg(text) {
      this.el.msg.textContent = text;
      this.el.msg.className = 'bkb__msg is-error';
      this.el.msg.hidden = false;
      if (mqMobile.matches) {
        window.scrollTo({ top: this.el.atcMain.getBoundingClientRect().top + window.scrollY - 120, behavior: mqReduce.matches ? 'auto' : 'smooth' });
      }
    }

    /* ---------- mobile sticky bar ---------- */
    setupSticky() {
      this._secIn = false;
      this._ctaIn = false;
      if (!('IntersectionObserver' in window)) return;
      const sec = new IntersectionObserver(([e]) => { this._secIn = e.isIntersecting; this.syncSticky(); });
      const cta = new IntersectionObserver(([e]) => { this._ctaIn = e.isIntersecting; this.syncSticky(); });
      sec.observe(this.querySelector('.bkb__inner'));
      cta.observe(this.el.atcMain);
      this._observers = [sec, cta];
      mqMobile.addEventListener('change', () => { this.syncSticky(); this.syncHint(); }, { signal: this._abort.signal });
    }
    // Mobile kit bar: shown while the section is on screen but its own add-to-cart button isn't.
    syncSticky() {
      const show = this.kitReady && mqMobile.matches && this._secIn && !this._ctaIn && !this.pickMode;
      this.el.sticky.hidden = !show;
      // The theme's product sticky bar and the Smile.io rewards launcher would cover the kit, so they stay
      // hidden while the section is on screen (any width) and come back above or below it.
      document.body.classList.toggle('bkb-in-view', !!this._secIn);
    }
  }

  customElements.define('bundle-kit-builder', BundleKitBuilder);
}
